import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/subscriptions", label: "Subscriptions" },
  { to: "/pricing", label: "Get a Quote" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 border-b ${
        scrolled || open ? "bg-black/85 backdrop-blur-md border-white/10" : "bg-black/40 backdrop-blur-md border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 group">
          <img
            src="/arsenal-artists-logo.png"
            alt="Arsenal Artists emblem"
            className="h-9 w-9 sm:h-11 sm:w-11 object-contain transition-transform duration-300 group-hover:rotate-[-4deg] group-hover:scale-105"
          />
          <span className="font-display font-800 font-extrabold tracking-[0.18em] text-sm sm:text-base text-white">
            ARSENAL <span className="text-arsenal">ARTISTS</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-[13px] font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-white" : "text-slate-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] bg-arsenal transition-transform duration-300 origin-left ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to={user ? "/portal" : "/login"}
            data-testid="nav-portal-link"
            className="hidden sm:inline-flex text-[13px] font-semibold tracking-wide text-slate-400 hover:text-white transition-colors"
          >
            {user ? "Client Portal" : "Log in"}
          </Link>
          <Link
            to="/start-a-project"
            data-testid="nav-start-project-btn"
            className="clip-btn hidden sm:inline-flex items-center gap-1.5 bg-arsenal hover:bg-arsenal-bright text-white text-[13px] font-bold tracking-wider px-5 py-2.5 transition-colors duration-200"
          >
            START A PROJECT <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10"
            aria-label="Mobile"
          >
            <div className="px-5 py-4 flex flex-col">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 text-base font-semibold border-b border-white/5 ${isActive ? "text-arsenal" : "text-slate-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to={user ? "/portal" : "/login"} data-testid="nav-mobile-portal" className="py-3 text-base font-semibold text-slate-300 border-b border-white/5">
                {user ? "Client Portal" : "Log in"}
              </Link>
              <Link
                to="/start-a-project"
                data-testid="nav-mobile-start-project"
                className="mt-4 clip-btn inline-flex items-center justify-center gap-2 bg-arsenal text-white font-bold tracking-wider px-5 py-3.5"
              >
                START A PROJECT <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
